import {
  fetchShopApplications,
  fetchShopApplicationsCSV,
  ShopApplicationListResponse,
} from 'admin/actions/shop-applications';
import { createBadge } from 'admin/components/atoms/Badge';
import { Button } from 'admin/components/atoms/Buttons';
import FA from 'admin/components/atoms/FontAwesome';
import Table from 'admin/components/atoms/Table';
import BreadCrumb, { CrumbType } from 'admin/components/molecules/BreadCrumb';
import Pager from 'admin/components/shared/Pager';
import type { Moment } from 'moment';
import React, { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import styled from 'styled-components';
import { useAppSelector } from '~/javascripts/admin/hooks/useApp';
import { selectUserPrivateMoney } from '~/javascripts/admin/reducers/user-private-money';
import { downloadCsvFile, formatDate, formatUUID, toQueryParams } from '~/javascripts/admin/utils';
import DateTimeRange from '../../atoms/DateTimeRange';
import Headings from '../../atoms/Headings';
import Input from '../../atoms/Input';
import Label from '../../atoms/Label';
import Loader from '../../atoms/Loader';
import Select from '../../atoms/Select';
import SelectedPrivateMoneyDisplay from '../../organisms/SelectedPrivateMoneyDisplay';

const CRUMBS: CrumbType[] = [
  { value: 'ホーム', href: '/', isCurrentPage: false },
  { value: '店舗管理', href: '', isCurrentPage: true },
  { value: '加盟店舗申請管理', href: '', isCurrentPage: true },
];

const STATUS_OPTIONS = [
  { label: '指定なし', value: '' },
  { label: '申請受付済', value: 'submitted' },
  { label: '審査中', value: 'reviewing' },
  { label: '保留中', value: 'pending' },
  { label: '承認済み', value: 'approved' },
  { label: '却下', value: 'rejected' },
];

const STATUS_MAP = {
  submitted: {
    label: '申請受付済',
    color: '#fff',
    bgColor: '#2684FF',
  },
  reviewing: {
    label: '審査中',
    color: '#fff',
    bgColor: '#E8890C',
  },
  pending: {
    label: '保留中',
    color: '#fff',
    bgColor: '#9A6700',
  },
  approved: {
    label: '申請承認済',
    color: '#fff',
    bgColor: '#21BF25',
  },
  rejected: {
    label: '却下',
    color: '#fff',
    bgColor: '#525252',
  },
};

const StatusBadge = createBadge(STATUS_MAP);

const PageHeader = styled(Headings.h5)`
  margin-bottom: 8px;
`;

const FilterSection = styled.div`
  display: flex;
  flex-wrap: wrap;
  align-items: flex-end;
  gap: 16px;
  margin-top: 24px;
  padding: 16px;
`;

const FilterItem = styled.div`
  display: flex;
  gap: 4px;
  min-width: 160px;
  align-items: center;
  ${Label} {
    margin-bottom: 0;
  }
`;

const SearchResultSection = styled.div`
  display: flex;
  align-items: center;
  gap: 16px;
  margin-top: 24px;
`;

const SearchResultTitle = styled(Headings.h5)`
  margin: 0;
`;

const SearchResultCount = styled.span`
  color: #666;
`;

const CsvButtonWrapper = styled.div`
  margin-left: auto;
`;

const LoaderWrapper = styled.div`
  width: 100%;
  height: 150px;
  display: flex;
  justify-content: center;
  align-items: center;
`;

const TableWrapper = styled.div`
  margin-top: 16px;
`;

const StyledTable = styled(Table)``;

const StyledLink = styled(Link)`
  color: #007bff;
  text-decoration: none;
  &:hover {
    text-decoration: underline;
  }
`;

const PagerWrapper = styled.div`
  margin-top: 16px;
`;

const ShopApplications = () => {
  const userPrivateMoney = useAppSelector(selectUserPrivateMoney);

  // UI state (what the user is currently editing in the filter form)
  const [statusFilter, setStatusFilter] = useState<string>('');
  const [uuidPrefix, setUuidPrefix] = useState<string>('');
  const [organizationName, setOrganizationName] = useState<string>('');
  const [fromDate, setFromDate] = useState<Moment | null>(null);
  const [toDate, setToDate] = useState<Moment | null>(null);

  // Applied state (updated only when 検索 is clicked; drives fetch and CSV export)
  const [appliedStatus, setAppliedStatus] = useState<string>('');
  const [appliedUuidPrefix, setAppliedUuidPrefix] = useState<string>('');
  const [appliedOrganizationName, setAppliedOrganizationName] = useState<string>('');
  const [appliedFromDate, setAppliedFromDate] = useState<Moment | null>(null);
  const [appliedToDate, setAppliedToDate] = useState<Moment | null>(null);

  const [isLoading, setIsLoading] = useState(false);
  const [applications, setApplications] = useState<ShopApplicationListResponse | null>(null);

  const loadApplications = useCallback(
    async (page = 1) => {
      setIsLoading(true);
      try {
        const queryString = toQueryParams(
          {
            status: appliedStatus || null,
            uuid_prefix: appliedUuidPrefix || null,
            organization_name: appliedOrganizationName || null,
            from: appliedFromDate || null,
            to: appliedToDate || null,
            page,
          },
          true
        );
        const response = await fetchShopApplications(queryString);
        setApplications(response);
      } catch (err) {
        console.error('Failed to fetch shop applications', err);
      } finally {
        setIsLoading(false);
      }
    },
    [appliedStatus, appliedUuidPrefix, appliedOrganizationName, appliedFromDate, appliedToDate]
  );

  useEffect(() => {
    loadApplications();
  }, [
    userPrivateMoney.id,
    appliedStatus,
    appliedUuidPrefix,
    appliedOrganizationName,
    appliedFromDate,
    appliedToDate,
    loadApplications,
  ]);

  const handleSearch = () => {
    setAppliedStatus(statusFilter);
    setAppliedUuidPrefix(uuidPrefix);
    setAppliedOrganizationName(organizationName);
    setAppliedFromDate(fromDate);
    setAppliedToDate(toDate);
  };

  const onChangePage = (page: number) => {
    loadApplications(page);
  };

  const handleDownloadCsv = () => {
    const params: Record<string, unknown> = {};
    if (appliedStatus) params.status = appliedStatus;
    if (appliedUuidPrefix) params.uuid_prefix = appliedUuidPrefix;
    if (appliedOrganizationName) params.organization_name = appliedOrganizationName;
    if (appliedFromDate) params.from = appliedFromDate;
    if (appliedToDate) params.to = appliedToDate;
    fetchShopApplicationsCSV(toQueryParams(params, true)).then((res) => {
      downloadCsvFile(res.url);
    });
  };

  return (
    <>
      <BreadCrumb crumbs={CRUMBS} />
      <PageHeader>加盟店申請検索</PageHeader>
      <SelectedPrivateMoneyDisplay />

      <FilterSection>
        <FilterItem>
          <Label>進捗状態</Label>
          <Select
            options={STATUS_OPTIONS}
            value={STATUS_OPTIONS.find((opt) => opt.value === statusFilter) ?? STATUS_OPTIONS[0]}
            onChange={(opt) => setStatusFilter(opt?.value ?? '')}
          />
        </FilterItem>
        <FilterItem>
          <Label>申請番号</Label>
          <Input
            value={uuidPrefix}
            onChange={(e: React.ChangeEvent<HTMLInputElement>) => setUuidPrefix(e.target.value)}
          />
        </FilterItem>
        <FilterItem>
          <Label>申請受付日</Label>
          <DateTimeRange
            dateRange={{ from: fromDate, to: toDate }}
            setDateRange={({ from, to }) => {
              setFromDate(from ?? null);
              setToDate(to ?? null);
            }}
            dateTimeProps={{
              fromProps: { showIcon: true },
              toProps: { showIcon: true },
            }}
            wrapperStyles="margin-bottom: 0;"
          />
        </FilterItem>
        <FilterItem>
          <Label>組織名</Label>
          <Input
            value={organizationName}
            onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
              setOrganizationName(e.target.value)
            }
          />
        </FilterItem>
      </FilterSection>
      <Button onClick={handleSearch}>検索</Button>

      <SearchResultSection>
        <SearchResultTitle>検索結果</SearchResultTitle>
        <SearchResultCount>件数: {applications?.count ?? 0}</SearchResultCount>
        <CsvButtonWrapper>
          <Button onClick={handleDownloadCsv} variant="muted-outline">
            <FA icon="download" /> CSV
          </Button>
        </CsvButtonWrapper>
      </SearchResultSection>

      <TableWrapper>
        {isLoading ? (
          <LoaderWrapper>
            <Loader />
          </LoaderWrapper>
        ) : (
          <>
            <StyledTable>
              <Table.Header>
                <Table.Row>
                  <Table.Cell width="150px">進捗状態</Table.Cell>
                  <Table.Cell width="150px">申請番号</Table.Cell>
                  <Table.Cell width="150px">申請受付日</Table.Cell>
                  <Table.Cell>申請者</Table.Cell>
                  <Table.Cell>組織名</Table.Cell>
                  <Table.Cell>店舗情報</Table.Cell>
                  <Table.Cell width="130px">電話番号</Table.Cell>
                </Table.Row>
              </Table.Header>
              <Table.Body>
                {applications?.rows.map((app) => (
                  <Table.Row
                    key={app.id}
                    style={{
                      background: app.status === 'rejected' ? '#ddd' : 'unset',
                    }}
                  >
                    <Table.Cell>
                      <StatusBadge status={app.status} />
                    </Table.Cell>
                    <Table.Cell>
                      <StyledLink to={`/shops/applications/${app.id}`}>
                        {formatUUID(app.id)}
                      </StyledLink>
                    </Table.Cell>
                    <Table.Cell>
                      {app.applied_at ? formatDate(app.applied_at, 'YYYY/MM/DD') : '-'}
                    </Table.Cell>
                    <Table.Cell>{app.contact_person_name || '-'}</Table.Cell>
                    <Table.Cell>{app.organization_name || '-'}</Table.Cell>
                    <Table.Cell>
                      {app.shop_name}
                      {app.shop_name_furigana && (
                        <span style={{ color: '#666', marginLeft: 8 }}>
                          ({app.shop_name_furigana})
                        </span>
                      )}
                    </Table.Cell>
                    <Table.Cell>{app.tel || '-'}</Table.Cell>
                  </Table.Row>
                ))}
                {(!applications || applications.rows.length === 0) && (
                  <Table.Row>
                    <Table.Cell colSpan={7} align="center">
                      データがありません
                    </Table.Cell>
                  </Table.Row>
                )}
              </Table.Body>
            </StyledTable>

            {applications?.pagination && (
              <PagerWrapper>
                <Pager pagination={applications.pagination} onChangePage={onChangePage} />
              </PagerWrapper>
            )}
          </>
        )}
      </TableWrapper>
    </>
  );
};

export default ShopApplications;
